/**
 * Login page.
 *
 * Authenticates via POST /api/auth/login. If the account has MFA enabled, the
 * server returns an { mfaRequired, mfaToken } challenge instead of tokens — the
 * page then shows a second step to enter the 6-digit code (or a recovery code),
 * completed via POST /api/auth/mfa-verify.
 *
 * @module pages/Login
 */

import { useState, useEffect, type ReactElement, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../api/client';
import { MarketingLayout } from '../components/MarketingLayout';
import { PasswordInput } from '../components/PasswordInput';
import { IDLE_LOGOUT_FLAG } from '../components/IdleTimeout';
import type { LoginResponseData, LoginResult } from '../types';

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ' +
  'disabled:bg-gray-50 disabled:text-gray-500';

export default function Login(): ReactElement {
  const { token, user, login, isLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // MFA challenge step
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  // Success banner after completing a password reset (/login?reset=1).
  const [searchParams] = useSearchParams();
  const resetDone = searchParams.get('reset') === '1';

  // One-time notice when the previous session ended on an inactivity timeout.
  const [idleNotice, setIdleNotice] = useState(false);
  useEffect((): void => {
    if (sessionStorage.getItem(IDLE_LOGOUT_FLAG)) {
      sessionStorage.removeItem(IDLE_LOGOUT_FLAG);
      setIdleNotice(true);
    }
  }, []);

  // Already authenticated — send to the right home for the role
  if (!isLoading && token) {
    return <Navigate to={user?.role === 'employee' ? '/portal' : '/dashboard'} replace />;
  }

  const finishLogin = (data: LoginResponseData): void => {
    login(data.tokens.accessToken, data.user, data.org);
    navigate(data.user.role === 'employee' ? '/portal' : '/dashboard', { replace: true });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError('Email is required.');
    if (!password) return setError('Password is required.');

    setSubmitting(true);
    try {
      const result = await apiRequest<LoginResult>('/api/auth/login', null, {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!result.ok) return setError(result.message);

      if ('mfaRequired' in result.data) {
        setMfaToken(result.data.mfaToken);
      } else {
        finishLogin(result.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfa = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!mfaCode.trim()) return setError('Enter the code from your authenticator app.');

    setSubmitting(true);
    try {
      const result = await apiRequest<LoginResponseData>('/api/auth/mfa-verify', null, {
        method: 'POST',
        body: JSON.stringify({ mfaToken, code: mfaCode.trim() }),
      });
      if (!result.ok) return setError(result.message);
      finishLogin(result.data);
    } finally {
      setSubmitting(false);
    }
  };

  // Marketing header/footer stay visible so a visitor can hop to Features or
  // Pricing and back without losing the sign-in page.
  return (
    <MarketingLayout>
      <div className="flex-1 bg-gray-50">
        <div className="mx-auto w-full max-w-sm px-4 py-16">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {mfaToken ? (
            // ── MFA step ──────────────────────────────────────────────────
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Two-factor authentication</h2>
              <p className="mb-6 text-sm text-gray-500">
                Enter the 6-digit code from your authenticator app (or a recovery code).
              </p>
              <form onSubmit={(e) => void handleMfa(e)} noValidate>
                <input
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  disabled={submitting}
                  className={inputClass + ' text-center tracking-widest'}
                  placeholder="123456"
                />
                {error && (
                  <div role="alert" className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 w-full rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium py-2.5 text-sm transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Verifying…' : 'Verify'}
                </button>
              </form>
              <button
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode('');
                  setError(null);
                }}
                className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to sign in
              </button>
            </>
          ) : (
            // ── Password step ─────────────────────────────────────────────
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-6">Sign in</h2>
              {resetDone && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                  Your password has been updated. Please sign in with your new password.
                </div>
              )}
              {idleNotice && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  You were signed out due to inactivity. Please sign in again.
                </div>
              )}
              <form onSubmit={(e) => void handleSubmit(e)} noValidate>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    className={inputClass}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                    <Link to="/forgot-password" className="text-xs font-medium text-brand-600 hover:text-brand-700">
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                    className={inputClass}
                    placeholder="••••••••"
                  />
                </div>
                {error && (
                  <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium py-2.5 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
              <p className="mt-6 text-center text-sm text-gray-500">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">Create one</Link>
              </p>
            </>
          )}
        </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
